import type { CRMTask, CRMTaskReference, TaskStage, TaskVisibility } from "@/types";
import { TYPENAME_TO_REFERENCEABLE_TYPE } from "@/features/global/components/NotesTable/mutations";

export const TASK_ANALYTICS_EVENTS = {
  tabSelected: "task tab selected",
  listFiltered: "task list filtered",
  opened: "task opened",
  created: "task created",
  updated: "task updated",
  stageChanged: "task stage changed",
  deleted: "task deleted",
  referenceAdded: "task reference added",
  referenceRemoved: "task reference removed"
} as const;

export type TaskAnalyticsSource =
  | "global_create"
  | "person_record"
  | "my_tasks_tab"
  | "department_tasks_tab"
  | "embedded_tasks_table"
  | "task_detail"
  | "task_side_sheet"
  | "dashboard_quick_add"
  | "dashboard";

type TaskAnalyticsTask = Partial<Pick<CRMTask, "id" | "stage" | "visibility" | "dueDate" | "references">>;

export function getTaskAnalyticsData(
  task: TaskAnalyticsTask,
  source: TaskAnalyticsSource,
  options: { includeStage?: boolean } = {}
) {
  const references = task.references ?? [];
  const { includeStage = true } = options;

  return {
    taskId: task.id,
    source,
    ...(includeStage ? { stage: task.stage } : {}),
    visibility: task.visibility,
    hasDueDate: Boolean(task.dueDate),
    referenceCount: references.length,
    referenceTypes: getReferenceTypes(references)
  };
}

export type CreatedTaskAnalyticsInput = {
  taskId?: string;
  source: TaskAnalyticsSource;
  stage?: TaskStage;
  visibility?: TaskVisibility;
  dueDate?: string | null;
  referenceTypes?: string[];
  hasAssignee?: boolean;
  teamTagCount?: number;
};

export function getCreatedTaskAnalyticsData({
  taskId,
  source,
  stage,
  visibility,
  dueDate,
  referenceTypes,
  hasAssignee,
  teamTagCount
}: CreatedTaskAnalyticsInput) {
  return {
    taskId,
    source,
    stage,
    visibility,
    hasDueDate: Boolean(dueDate),
    referenceCount: referenceTypes?.length ?? 0,
    referenceTypes: referenceTypes ?? [],
    hasAssignee: Boolean(hasAssignee),
    teamTagCount: teamTagCount ?? 0
  };
}

export function getReferenceTypes(references: Pick<CRMTaskReference, "__typename">[]) {
  return Array.from(
    new Set(
      references.flatMap((reference) => {
        const referenceableType = TYPENAME_TO_REFERENCEABLE_TYPE[reference.__typename];
        return referenceableType ? [referenceableType] : [];
      })
    )
  ).sort();
}
